<template>
  <div id="app" :style="appStyle">
    <div class="scroll-container" ref="scrollContainer">
      <transition name="slide-left" mode="out-in">
        <RouterView />
      </transition>
    </div>
    <LogoutPopupOne v-if="themeStore.themeIndex === 1" ref="logOutRef" />
    <LogoutPopupTwo v-if="themeStore.themeIndex === 2" ref="logOutRef" />
    <LogoutPopupThree v-if="themeStore.themeIndex === 3" ref="logOutRef" />
    <LogoutPopupFour v-if="themeStore.themeIndex === 4" ref="logOutRef" />
    <RegisterLoginPopup ref="registerRef" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, provide, ref, watch } from 'vue';
import RegisterLoginPopup from './view/loginRegister/registerLoginPopup.vue';
import { playClickSound } from './utils/media';
import { useThemeStore } from './stores/theme';
import { useUserStore } from './stores/user';
import LogoutPopupOne from './themes/themeOne/profile/components/logoutPopup.vue';
import LogoutPopupTwo from './themes/themeTwo/profile/components/logoutPopup.vue';
import LogoutPopupThree from './themes/themeThree/profile/components/logoutPopup.vue';
import LogoutPopupFour from './themes/themeFour/profile/components/logoutPopup.vue';
import { subscribeUserToPush } from './utils/push';
import { useRoute } from 'vue-router';
import { initFacebookPixel } from './utils/facebookTracking';
const logOutRef = ref()
const registerRef = ref()
const themeStore = useThemeStore()
const userStore = useUserStore()

const route = useRoute();
const scrollContainer = ref<HTMLElement | null>(null);
provide('openLogoutPopup', () => {
  logOutRef.value.open()
});

provide('openRegisterLoginPopup', (type: number) => {
  playClickSound()
  registerRef.value.open(type)
});

(window as any).openLoginPopup = (type: number) => {
  playClickSound();
  registerRef.value.open(type);
};

const updateFavicon = (themeIndex: number) => {
  let faviconUrl = '';
  try {
    if (themeIndex === 1) {
      faviconUrl = new URL('@/assets/home/APP_gold.png', import.meta.url).href;
    } else if (themeIndex === 2) {
      faviconUrl = new URL('@/assets/themetwo/logo2.png', import.meta.url).href;
    } else if (themeIndex === 3) {
      faviconUrl = new URL('@/assets/themeThree/logo.png', import.meta.url).href;
    } else if (themeIndex === 4) {
      faviconUrl = new URL('@/assets/themeFour/APP6.png', import.meta.url).href;
    } else {
      faviconUrl = '/head_icon.jpg';
    }

    const links = document.querySelectorAll("link[rel*='icon']");
    links.forEach(link => {
      (link as HTMLLinkElement).href = faviconUrl;
    });
  } catch (error) {
    console.error("Error updating favicon:", error);
  }
}
const applyBodyBackground = () => {
  if (themeStore.themeIndex === 1) {
    document.body.style.backgroundColor = '#27190d'
  } else if (themeStore.themeIndex === 2) {
    document.body.style.backgroundColor = 'rgb(49, 39, 52)'
  } else if (themeStore.themeIndex === 3) {
    document.body.style.backgroundColor = '#090F1F'
  } else if (themeStore.themeIndex === 4) {
    document.body.style.backgroundColor = 'rgb(0, 97, 96)'
  } else {
    document.body.style.backgroundColor = '#27190d'
  }
}
// Watch for user login to subscribe to push notifications
watch(() => userStore.id, (newId, oldId) => {
  if (newId && !oldId) {
    subscribeUserToPush();
  }
}, { immediate: true }); // Use immediate to check on initial load

watch(() => themeStore.themeIndex, (newIndex) => {
  applyBodyBackground();
  updateFavicon(newIndex);
}, { immediate: true });

watch(() => route.path, () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = 0;
  }
});
const appStyle = computed(() => {
  let style: Record<string, string> = {};

  if (themeStore.themeIndex === 1) {
    style.backgroundImage = `url(${new URL('@/assets/main_bg.png', import.meta.url).href})`;
    style.backgroundSize = 'cover';
    style.backgroundRepeat = 'repeat';
    style.backgroundColor = '#27190d';
  } else if (themeStore.themeIndex === 2) {
    style.backgroundColor = '#221b24';
    style.backgroundImage = 'none';
    style.backgroundSize = 'cover';
    style.backgroundRepeat = 'repeat';
  } else if (themeStore.themeIndex === 3) {
    style.backgroundColor = '#090f1f';
    style.backgroundImage = `url(${new URL('@/assets/texture_bg.png', import.meta.url).href})`;
    style.backgroundSize = 'initial';
    style.backgroundRepeat = 'repeat';
  } else if (themeStore.themeIndex === 4) {
    style.backgroundColor = '#002d2d';
    style.backgroundImage = 'none';
    style.backgroundSize = 'cover';
    style.backgroundRepeat = 'repeat';
    style.fontFamily = 'system-ui';
  } else {
    style.backgroundColor = '#27190d';
    style.backgroundImage = `url(${new URL('@/assets/main_bg.png', import.meta.url).href})`;
    style.backgroundSize = 'cover';
    style.backgroundRepeat = 'repeat';
  }

  return style;
});
onMounted(() => {
  applyBodyBackground()
  initFacebookPixel()
})
</script>

<style>
#app {
  position: fixed;
  width: 28rem;
  height: 100%;
  left: 50%;
  transform: translate3d(-50%, 0, 0);
  -webkit-transform: translate3d(-50%, 0, 0);
  overflow-x: hidden;
  overflow-y: hidden;
  background-image: url('@/assets/main_bg.png');
  background-size: cover;
  background-repeat: repeat;
  background-color: #312734;
}

@media (min-width: 1440px) {
  #app {
    width: 30rem;
  }
}

@media (min-width: 1920px) {
  #app {
    width: 35rem;
  }
}

.scroll-container {
  height: 100%;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scroll-container::-webkit-scrollbar {
  display: none;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.5s ease;
  position: absolute;
  width: 100%;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-enter-to {
  transform: translateX(0%);
  opacity: 1;
}

.slide-left-leave-from {
  transform: translateX(0%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

.ql-align-center {
  text-align: center;
}

.ql-align-right {
  text-align: right;
}

.ql-align-left {
  text-align: left;
}

@media screen and (max-width: 600px) {
  #app {
    width: 100%;
    left: 0;
    transform: none;
    -webkit-transform: none;
  }
}
</style>
